import React, { useState, useEffect } from "react";
import { database } from "../../firebaseConfig";
import { ref, set, push, update, remove, onValue, query, orderByChild, get } from "firebase/database";
import ServiceForm from "./ServiceForm";
import OfferServiceItem from "./OfferServiceItem.js";
import { calculateOfferPricing } from "./utils/offerPricing";
import "./AddOfferServices/AddOfferServices.css";

const AddOfferServices = ({ userId }) => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt"); // createdAt, title, price
  const [sortDirection, setSortDirection] = useState("desc"); // asc, desc
  const [notification, setNotification] = useState({ message: "", type: "" });

  // Loading services from the database
  useEffect(() => {
    if (!userId) return;

    const servicesRef = ref(database, `/World/${userId}/services`);
    
    const unsubscribe = onValue(servicesRef, (snapshot) => {
      if (snapshot.exists()) {
        const servicesData = snapshot.val();
        const servicesArray = Object.entries(servicesData).map(([id, data]) => ({
          id,
          ...data
        }));
        setServices(servicesArray);
        
        // Extract categories
        const uniqueCategories = [...new Set(servicesArray
          .map(service => service.category)
          .filter(category => category && category.trim() !== "")
        )];
        setCategories(uniqueCategories);
      } else {
        setServices([]);
        setCategories([]);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Trigger resync of all public offers when services are updated
  const triggerPublicOffersResync = async () => {
    try {
      // Get all offers
      const offersRef = ref(database, `/World/${userId}/offers`);
      const offersSnapshot = await get(offersRef);
      
      if (!offersSnapshot.exists()) return;
      
      const offers = offersSnapshot.val();
      
      // Find and resync all public offers
      for (const [offerId, offerData] of Object.entries(offers)) {
        if (offerData.isPublic) {
          await resyncPublicOffer(offerId, offerData);
        }
      }
      
      console.log("✅ Resynced all public offers after service update");
    } catch (error) {
      console.error("Error resyncing public offers:", error);
    }
  };

  // Resync a single public offer
  const resyncPublicOffer = async (offerId, offerData) => {
    try {
      const customId = `${userId}+${offerId}`;
      
      // Get updated services data
      let servicesData = [];
      if (offerData.services && offerData.services.length > 0) {
        const servicesRef = ref(database, `/World/${userId}/services`);
        const servicesSnapshot = await get(servicesRef);
        
        if (servicesSnapshot.exists()) {
          const allServices = servicesSnapshot.val();
          servicesData = offerData.services
            .map(serviceId => {
              const service = allServices[serviceId];
              return service ? { id: serviceId, ...service } : null;
            })
            .filter(service => service !== null);
        }
      }
      
      // Get team member data if available
      let teamMemberData = null;
      if (offerData.teamMemberId) {
        const teamMemberRef = ref(database, `/World/${userId}/team/members/${offerData.teamMemberId}`);
        const teamMemberSnapshot = await get(teamMemberRef);
        if (teamMemberSnapshot.exists()) {
          teamMemberData = teamMemberSnapshot.val();
        }
      }
      
      // Calculate pricing with updated services
      const pricing = calculateOfferPricing(offerData, servicesData);
      
      // Update public offer with fresh data including recalculated pricing
      const publicOfferData = {
        ...offerData,
        services: servicesData,
        teamMember: teamMemberData,
        userId,
        offerId,
        lastUpdated: Date.now(),
        // Include recalculated pricing
        ...pricing
      };
      
      const publicOfferRef = ref(database, `/publicOffers/${customId}`);
      await set(publicOfferRef, publicOfferData);
    } catch (error) {
      console.error(`Error resyncing public offer ${offerId}:`, error);
    }
  };

  // Save service (new or updated)
  const handleSaveService = (serviceData) => {
    if (!userId) return;

    if (editingService) {
      // Update service
      const serviceRef = ref(database, `/World/${userId}/services/${editingService.id}`);
      update(serviceRef, serviceData)
        .then(() => {
          showNotification("Service successfully updated", "success");
          setEditingService(null);
          setShowForm(false);
          // Trigger a re-sync of all public offers
          triggerPublicOffersResync();
        })
        .catch(error => {
          console.error("Error updating service:", error);
          showNotification("Error updating service", "error");
        });
    } else {
      // Create new service
      const servicesRef = ref(database, `/World/${userId}/services`);
      const newServiceRef = push(servicesRef);
      set(newServiceRef, serviceData)
        .then(() => {
          showNotification("Service successfully created", "success");
          setShowForm(false);
        })
        .catch(error => {
          console.error("Error creating service:", error);
          showNotification("Error creating service", "error");
        });
    }
  };

  // Prepare service for editing
  const handleEditService = (service) => {
    setEditingService(service);
    setShowForm(true);
  };

  // Delete service
  const handleDeleteService = (serviceId) => {
    if (!userId) return;
    
    if (window.confirm("Are you sure you want to delete this service?")) {
      const serviceRef = ref(database, `/World/${userId}/services/${serviceId}`);
      remove(serviceRef)
        .then(() => {
          showNotification("Service successfully deleted", "success");
        })
        .catch(error => {
          console.error("Error deleting service:", error);
          showNotification("Error deleting service", "error");
        });
    }
  };

  // Duplicate service
  const handleDuplicateService = (service) => {
    if (!userId) return;

    // Create a copy of the service with modified title
    const duplicatedService = {
      ...service,
      title: `${service.title} (Copy)`,
      createdAt: Date.now() // Update creation timestamp
    };

    // Remove the old ID since Firebase will generate a new one
    delete duplicatedService.id;

    // Create new service in database
    const servicesRef = ref(database, `/World/${userId}/services`);
    const newServiceRef = push(servicesRef);
    set(newServiceRef, duplicatedService)
      .then(() => {
        showNotification("Service successfully duplicated", "success");
      })
      .catch(error => {
        console.error("Error duplicating service:", error);
        showNotification("Error duplicating service", "error");
      });
  };

  // Show notification and automatically hide it
  const showNotification = (message, type) => {
    setNotification({ message, type });
    
    // Automatically hide after 3 seconds
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  // Filter and sort services
  const getFilteredAndSortedServices = () => {
    // Filter by search term and category
    const filtered = services.filter(service => {
      const matchesSearch = searchTerm.trim() === "" || 
        service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = categoryFilter === "" || service.category === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });
    
    // Sort by the selected criterion
    return filtered.sort((a, b) => {
      if (sortBy === "price") {
        return sortDirection === "asc" ? a.price - b.price : b.price - a.price;
      } else if (sortBy === "title") {
        return sortDirection === "asc" 
          ? a.title.localeCompare(b.title) 
          : b.title.localeCompare(a.title);
      } else { // createdAt
        return sortDirection === "asc" 
          ? a.createdAt - b.createdAt 
          : b.createdAt - a.createdAt;
      }
    });
  };

  const filteredAndSortedServices = getFilteredAndSortedServices();

  // Change sorting
  const handleSortChange = (field) => {
    if (sortBy === field) {
      // If already sorted by this field, reverse sort direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Otherwise sort by the new field (default descending)
      setSortBy(field);
      setSortDirection("desc");
    }
  };

  return (
    <div className="add-offer-services">
      {notification.message && (
        <div className={`add-offer-services__notification add-offer-services__notification--${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      <div className="add-offer-services__header">
        <h2 className="add-offer-services__title">Manage Services</h2>
        {!showForm && (
          <button 
            className="add-offer-services__add-button" 
            onClick={() => {
              setEditingService(null);
              setShowForm(true);
            }}
          >
            <span className="add-offer-services__btn-icon">+</span> Create New Service
          </button>
        )}
      </div>
      
      {showForm ? (
        <ServiceForm 
          onSubmit={handleSaveService} 
          initialService={editingService}
          isEditing={!!editingService}
          onCancel={() => {
            setEditingService(null);
            setShowForm(false);
          }}
          userId={userId}
        />
      ) : (
        <>
          <div className="add-offer-services__filters">
            <div className="add-offer-services__search-container">
              <input 
                type="text" 
                placeholder="Search services..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="add-offer-services__search-input"
              />
              {searchTerm && (
                <button 
                  className="add-offer-services__clear-search" 
                  onClick={() => setSearchTerm("")}
                >
                  ×
                </button>
              )}
            </div>
            
            <div className="add-offer-services__category-filter">
              <select 
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="add-offer-services__category-select"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="add-offer-services__sort-options">
            <span className="add-offer-services__sort-label">Sort by:</span>
            <button 
              className={`add-offer-services__sort-button ${sortBy === "createdAt" ? "active" : ""}`}
              onClick={() => handleSortChange("createdAt")}
            >
              Date {sortBy === "createdAt" && (sortDirection === "asc" ? "↑" : "↓")}
            </button>
            <button 
              className={`add-offer-services__sort-button ${sortBy === "title" ? "active" : ""}`}
              onClick={() => handleSortChange("title")}
            >
              Title {sortBy === "title" && (sortDirection === "asc" ? "↑" : "↓")}
            </button>
            <button 
              className={`add-offer-services__sort-button ${sortBy === "price" ? "active" : ""}`}
              onClick={() => handleSortChange("price")}
            >
              Price {sortBy === "price" && (sortDirection === "asc" ? "↑" : "↓")}
            </button>
          </div>
          
          <div className="add-offer-services__list">
            {isLoading ? (
              <div className="add-offer-services__loading">Loading services...</div>
            ) : filteredAndSortedServices.length > 0 ? (
              filteredAndSortedServices.map(service => (
                <OfferServiceItem 
                  key={service.id} 
                  service={service}
                  onEdit={() => handleEditService(service)}
                  onDelete={() => handleDeleteService(service.id)}
                  onDuplicate={() => handleDuplicateService(service)}
                />
              ))
            ) : (
              <div className="add-offer-services__empty-state">
                {searchTerm || categoryFilter ? 
                  "No services found matching your filter criteria." : 
                  "No services available yet. Create a new service using the button above."
                }
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AddOfferServices; 